"use client";

import {
  RegulatoryFormItem,
  regulatoryFormsData,
} from "@/data/regulatoryFormsData";
import { createSlug } from "@/utils/createSlug";
import { getInitialExpandedItems } from "@/utils/getInitialExpandedItems";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { IoIosArrowRoundForward } from "react-icons/io";
import { RiArrowDropDownLine, RiArrowDropUpLine } from "react-icons/ri";

interface FormItemRowProps {
  item: RegulatoryFormItem;
  isExpanded: boolean;
  onToggle: () => void;
  id: string;
}

function FormItemRow({ item, isExpanded, onToggle, id }: FormItemRowProps) {
  const isSingle = item.type === "single";

  const handleRowClick = () => {
    if (isSingle && item.href) {
      window.open(item.href, "_blank", "noopener,noreferrer");
    } else {
      onToggle();
    }
  };

  return (
    <div id={id} className="scroll-mt-24 border-b border-white/20">
      <div
        className="flex cursor-pointer items-center justify-between p-5 lg:py-[40px]"
        onClick={handleRowClick}
      >
        <h3 className="font-raleway text-[20px] font-bold text-white md:text-[32px]">
          {item.title}
        </h3>

        {isSingle ? (
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="border-brand-dark-red hover:bg-brand-dark-red flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <IoIosArrowRoundForward className="h-10 w-10 text-white" />
          </a>
        ) : (
          <button
            className={`flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-lg border transition-all ${
              isExpanded
                ? "border-brand-dark-red bg-brand-dark-red"
                : "border-brand-dark-red hover:bg-brand-dark-red"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            {isExpanded ? (
              <RiArrowDropUpLine className="h-10 w-10 text-white" />
            ) : (
              <RiArrowDropDownLine className="h-10 w-10 text-white" />
            )}
          </button>
        )}
      </div>

      {/* Expandable content for multiple type */}
      <AnimatePresence>
        {!isSingle && isExpanded && item.forms && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="space-y-3 pb-5 pl-4 md:pl-8">
              {item.forms.map((form, index) => (
                <motion.a
                  key={index}
                  href={form.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ x: -10, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:text-brand-red flex items-center gap-3 text-[20px] text-white/80 transition-colors"
                >
                  {/* <ArrowRight className="h-4 w-4" /> */}
                  {form.title}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FormsLinksSection() {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(
    getInitialExpandedItems
  );

  // Handle URL hash on mount and on hash change (for client-side navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the # symbol
      if (hash) {
        // Find the index of the item matching the hash
        const index = regulatoryFormsData.findIndex(
          (item) => createSlug(item.title) === hash
        );

        if (index !== -1) {
          const item = regulatoryFormsData[index];

          // If it's a multiple type, expand it
          if (item.type === "multiple") {
            setExpandedItems(new Set([index]));
          }

          // Scroll to the element after a small delay to ensure DOM is ready
          setTimeout(() => {
            const element = document.getElementById(hash);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }, 100);
        }
      }
    };

    // Run on mount if there's a hash
    if (window.location.hash) {
      handleHashChange();
    }

    // Listen for hash changes (client-side navigation)
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const toggleItem = (index: number) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <section className="bg-brand-black relative w-full px-4 py-8 md:py-[100px] lg:px-20">
      <div className="mx-auto max-w-[1200px]">
        {regulatoryFormsData.map((item, index) => (
          <FormItemRow
            key={index}
            item={item}
            isExpanded={expandedItems.has(index)}
            onToggle={() => toggleItem(index)}
            id={createSlug(item.title)}
          />
        ))}
      </div>
    </section>
  );
}
